import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { Checkbox } from '@mui/material';
import colors from '../../../styles/colors';
import semanticColors from '../../../styles/semanticColors';
import { SCAFFOLD_TAG_STYLES, isScaffoldTag } from '../core/scaffoldTagStyles';

/**
 * SelectableNode - 可勾選的節點組件
 * 鷹架標籤節點（summary / compare / question / insight / expand）會以專屬顏色與徽章標示
 *
 * @param {Object} data - 節點數據
 * @param {Function} handleClickOpenByNodeId - 點擊節點打開詳情的回調
 * @param {Set} selectedNodes - 已選中的節點 ID 集合
 * @param {Function} onToggleSelect - 切換節點選中狀態的回調
 */
export const SelectableNode = React.memo(({
  data,
  handleClickOpenByNodeId,
  selectedNodes = new Set(),
  onToggleSelect
}) => {
  const date = new Date(data.source.createdAt);
  const localTimeString = date.toLocaleString();
  const isLeafNode = data.isLeafNode;
  const nodeId = data.source.id?.toString();
  const isSelected = selectedNodes.has(nodeId);
  const tag = data.source?.tags || data.source?.group;
  const scaffoldStyle = tag && isScaffoldTag(tag) ? SCAFFOLD_TAG_STYLES[tag] : null;

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    if (onToggleSelect) {
      onToggleSelect(nodeId);
    }
  };

  const handleNodeClick = (e) => {
    e.stopPropagation();
    handleClickOpenByNodeId(data.source);
    sessionStorage.setItem('nodeId', data.source.id);
  };

  const outerBorder = isSelected
    ? `2px solid ${colors.primary.main}`
    : scaffoldStyle
      ? `2px solid ${scaffoldStyle.borderColor}`
      : '2px solid transparent';

  const innerBackground = isSelected
    ? colors.primary.background
    : scaffoldStyle
      ? scaffoldStyle.backgroundColor
      : colors.info.background;

  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 10,
        border: outerBorder,
        transition: 'all 0.2s ease',
      }}
    >
      <div
        style={{
          borderRadius: 10,
          background: innerBackground,
          cursor: 'pointer',
          textAlign: 'center',
          minWidth: 120,
          padding: 'max(1vw, 10px)',
          maxWidth: 500,
          overflow: 'hidden',
          position: 'relative',
          transition: 'all 0.2s ease',
        }}
      >
        {/* 勾選框 */}
        <div 
          style={{
            position: 'absolute',
            top: 4,
            left: 4,
            zIndex: 10,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={isSelected}
            onChange={handleCheckboxChange}
            size="small"
            sx={{
              padding: '2px',
              color: colors.primary.main,
              '&.Mui-checked': {
                color: colors.primary.main,
              },
            }}
            aria-label={`選擇節點: ${data.label}`}
          />
        </div>

        <div
          onClick={handleNodeClick}
          onTouchEnd={(e) => {
            if (e.defaultPrevented) return;
          }}
          style={{
            borderRadius: 10,
            cursor: 'pointer',
            textAlign: 'center',
            minWidth: 100,
            padding: 'max(1vw, 10px)',
            paddingTop: '24px', // 為勾選框留空間
            maxWidth: 200,
            overflow: 'hidden',
            touchAction: 'none',
            wordBreak: 'break-word',
          }}
        >
        {scaffoldStyle && (
          <div
            style={{
              display: 'inline-block',
              fontSize: '0.7rem',
              fontWeight: 600,
              color: scaffoldStyle.badgeColor,
              backgroundColor: 'rgba(255,255,255,0.85)',
              padding: '2px 8px',
              borderRadius: 8,
              marginBottom: 6,
              border: `1px solid ${scaffoldStyle.borderColor}`,
            }}
          >
            {scaffoldStyle.emoji} {scaffoldStyle.label}
          </div>
        )}
        <h3 style={{
          padding: "4px 2px",
          fontSize: "1rem", 
          fontWeight: "bold", 
          color: semanticColors.grayText,
          lineHeight: 1.5,
          margin: 0,
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          hyphens: 'auto',
        }}>
          {data.label}
        </h3>
        <div style={{
          fontSize: "0.85rem", 
          color: "#999",
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }}>
          By <strong>{data.source.author}</strong>    
        </div>
        <div style={{
          fontSize: "0.8rem", 
          color: "#999",
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }}>
          {localTimeString}
        </div>
        <Handle type="target" position={Position.Top} />
        <Handle type="source" position={Position.Bottom} />
      </div>
    </div>
    </div>
  );
}, (prevProps, nextProps) => {
  const prevNodeId = prevProps.data.source.id?.toString();
  const nextNodeId = nextProps.data.source.id?.toString();
  const prevSelected = prevProps.selectedNodes?.has(prevNodeId);
  const nextSelected = nextProps.selectedNodes?.has(nextNodeId);
  const prevTag = prevProps.data.source?.tags;
  const nextTag = nextProps.data.source?.tags;

  return prevProps.data.source.id === nextProps.data.source.id &&
         prevProps.data.isLeafNode === nextProps.data.isLeafNode &&
         prevProps.data.label === nextProps.data.label &&
         prevSelected === nextSelected &&
         prevTag === nextTag;
});

export default SelectableNode;
